import { brandLocales, localeName, pickLocale, type BrandConfig } from "@cfg/brand-sdk";
import { createDesignTranslator, parsePackLocaleFile, type DesignTranslator, type PackLocaleFile } from "@cfg/design-sdk";
import type { RegistryClient } from "./registry.ts";

/**
 * Language choice for the shell. The visitor's pick is kept in localStorage
 * under `LOCALE_STORAGE_KEY` (one site per brand, so one key is enough) and
 * beats the browser languages; stored values the brand doesn't offer are
 * ignored. Storage can be unavailable (private mode, blocked cookies), so every
 * access is guarded and the app works without it.
 */
export const LOCALE_STORAGE_KEY = "cfg-locale";

export function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeLocale(locale: string): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage unavailable: the choice lasts for this page view only
  }
}

/** Browser languages, most preferred first. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/** The locale to open with: stored choice, else browser languages, else the brand default. */
export function initialLocale(config: Pick<BrandConfig, "locale" | "locales">, stored = readStoredLocale(), preferred = browserLanguages()): string {
  return pickLocale({ available: brandLocales(config), fallback: config.locale, stored, preferred });
}

/** Switcher options: every offered locale with its native name. */
export function localeOptions(config: Pick<BrandConfig, "locale" | "locales">): { code: string; label: string }[] {
  return brandLocales(config).map((code) => ({ code, label: localeName(code) }));
}

/** `<html lang>` and the meta description follow the active locale. */
export function applyDocumentLocale(locale: string, description?: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  if (description) document.querySelector('meta[name="description"]')?.setAttribute("content", description);
}

/** Packs are authored in English; their locale catalogs translate from it. */
export const PACK_SOURCE_LANGUAGE = "en";

/**
 * Where a pack's files live, from one of its bundle URLs:
 * `<base>/<designId>/<version>/<hash>.js` -> `<base>` (same layout on the CDN and the dev server).
 */
export function packBaseUrl(bundleUrl: string): string | undefined {
  const m = /^(.*)\/[^/]+\/[^/]+\/[^/]+$/.exec(bundleUrl.split(/[?#]/)[0]);
  return m?.[1] || undefined;
}

/**
 * Translator for design text in `locale`, from `<pack base>/locales/<locale>.json`
 * of every pack in the catalog. Null for English (the source) or when no pack
 * has a catalog; a missing or broken file only leaves that pack in English.
 */
export async function loadDesignTranslator(registry: RegistryClient, locale: string): Promise<DesignTranslator | null> {
  if (locale.toLowerCase().split("-")[0] === PACK_SOURCE_LANGUAGE) return null;
  const entries = await registry.catalog();
  const bases = [...new Set(entries.map((e) => packBaseUrl(e.bundleUrl)).filter((b): b is string => !!b))];
  const files = await Promise.all(
    bases.map(async (base): Promise<PackLocaleFile | null> => {
      try {
        const res = await fetch(`${base}/locales/${encodeURIComponent(locale)}.json`, { cache: "no-cache" });
        return res.ok ? parsePackLocaleFile(await res.json()) : null;
      } catch (err) {
        console.warn(`No ${locale} text for designs at ${base}`, err);
        return null;
      }
    }),
  );
  const found = files.filter((f): f is PackLocaleFile => !!f);
  return found.length ? createDesignTranslator(locale, found) : null;
}
